/**
 * @quantabit/sdk-config - ConsentManager
 * 
 * 全局隐私同意管理器
 * 统一管理所有 SDK 的数据采集同意状态
 * 
 * 隐私等级:
 * - essential:   核心功能（无需同意，如认证、安全）
 * - functional:  功能增强（如语言偏好、主题设置）
 * - analytics:   数据分析（如页面浏览、点击追踪）
 * - marketing:   营销推广（如广告追踪、再营销）
 * 
 * 合规标准: GDPR, CCPA, ePrivacy
 */

import { logger } from './index.js';

// ============ 隐私等级定义 ============

export const PrivacyLevel = {
  ESSENTIAL: 'essential',       // 核心必需 — 不可关闭
  FUNCTIONAL: 'functional',     // 功能性 — 可选
  ANALYTICS: 'analytics',       // 数据分析 — 需同意
  MARKETING: 'marketing',       // 营销追踪 — 需同意
};

// ============ 同意状态 ============

export const ConsentStatus = {
  UNKNOWN: 'unknown',           // 用户尚未做出选择
  GRANTED: 'granted',           // 用户已同意
  DENIED: 'denied',             // 用户已拒绝
  WITHDRAWN: 'withdrawn',       // 用户撤回同意
};

// ============ 存储键 ============

const CONSENT_STORAGE_KEY = 'qbit_privacy_consent';
const CONSENT_TIMESTAMP_KEY = 'qbit_privacy_consent_ts';
const CONSENT_VERSION_KEY = 'qbit_privacy_consent_ver';

// 当前隐私政策版本 — 版本变更时会要求用户重新同意
const CURRENT_PRIVACY_VERSION = '1.0.0';

// ============ ConsentManager ============

class ConsentManagerClass {
  constructor() {
    this._consent = {
      [PrivacyLevel.ESSENTIAL]: ConsentStatus.GRANTED,   // 核心永远同意
      [PrivacyLevel.FUNCTIONAL]: ConsentStatus.UNKNOWN,
      [PrivacyLevel.ANALYTICS]: ConsentStatus.UNKNOWN,
      [PrivacyLevel.MARKETING]: ConsentStatus.UNKNOWN,
    };
    this._listeners = new Set();
    this._initialized = false;
    this._blockedCallbacks = new Map(); // SDK 名称 -> 被阻塞的回调列表
  }

  /**
   * 初始化同意管理器
   * 从 localStorage 恢复已保存的同意状态
   */
  init() {
    if (this._initialized) return this;
    
    // 初始化审计日志
    this._auditLog = [];

    if (typeof window !== 'undefined') {
      try {
        // 检查 GPC (Global Privacy Control) 信号 — CCPA §999.315
        if (navigator.globalPrivacyControl === true) {
          logger.info('[ConsentManager] GPC 信号检测到 — 自动拒绝非必要追踪');
          this._consent[PrivacyLevel.ANALYTICS] = ConsentStatus.DENIED;
          this._consent[PrivacyLevel.MARKETING] = ConsentStatus.DENIED;
          this._consent[PrivacyLevel.FUNCTIONAL] = ConsentStatus.GRANTED; // GPC 不影响功能性
          this._gpcDetected = true;
          this._persistConsent();
          this._addAuditEntry('gpc_detected', 'analytics,marketing auto-denied by GPC signal');
          this._initialized = true;
          return this;
        }

        // 检查隐私政策版本是否变更
        const savedVersion = localStorage.getItem(CONSENT_VERSION_KEY);
        if (savedVersion !== CURRENT_PRIVACY_VERSION) {
          // 隐私政策更新，需要用户重新同意
          logger.info('隐私政策版本已更新，需要重新同意');
          this._addAuditEntry('policy_version_change', `${savedVersion} → ${CURRENT_PRIVACY_VERSION}`);
          this._clearStoredConsent();
        } else {
          // 恢复已保存的同意状态
          const saved = localStorage.getItem(CONSENT_STORAGE_KEY);
          if (saved) {
            const parsed = JSON.parse(saved);
            Object.assign(this._consent, parsed);
            // 核心同意永远保持
            this._consent[PrivacyLevel.ESSENTIAL] = ConsentStatus.GRANTED;
          }
        }
      } catch (e) {
        logger.warn('无法恢复隐私同意状态:', e);
      }
    }

    this._initialized = true;
    return this;
  }

  /**
   * 获取指定等级的同意状态
   * @param {string} level - 隐私等级
   * @returns {string} 同意状态
   */
  getConsent(level) {
    return this._consent[level] || ConsentStatus.UNKNOWN;
  }

  /**
   * 获取所有同意状态
   * @returns {object} 完整的同意状态对象
   */
  getAllConsent() {
    return { ...this._consent };
  }

  /**
   * 检查是否已同意指定等级
   * @param {string} level - 隐私等级
   * @returns {boolean}
   */
  hasConsent(level) {
    if (level === PrivacyLevel.ESSENTIAL) return true;
    return this._consent[level] === ConsentStatus.GRANTED;
  }

  /**
   * 检查用户是否已做出选择（非 unknown 状态）
   * @returns {boolean}
   */
  hasUserDecided() {
    return Object.keys(this._consent)
      .filter(k => k !== PrivacyLevel.ESSENTIAL)
      .some(k => this._consent[k] !== ConsentStatus.UNKNOWN);
  }

  /**
   * 设置同意状态
   * @param {string} level - 隐私等级
   * @param {boolean} granted - 是否同意
   */
  setConsent(level, granted) {
    if (level === PrivacyLevel.ESSENTIAL) {
      logger.warn('核心功能同意不能被修改');
      return;
    }

    const newStatus = granted ? ConsentStatus.GRANTED : ConsentStatus.DENIED;
    const oldStatus = this._consent[level];
    this._consent[level] = newStatus;

    // 持久化
    this._persistConsent();

    // 审计日志
    this._addAuditEntry('consent_change', `${level}: ${oldStatus} → ${newStatus}`);

    // 通知监听器
    this._notifyListeners(level, newStatus, oldStatus);

    // 如果同意了，执行被阻塞的回调
    if (granted) {
      this._executeBlockedCallbacks(level);
    }

    logger.info(`隐私同意已更新: ${level} = ${newStatus}`);
  }

  /**
   * 接受所有
   */
  acceptAll() {
    [PrivacyLevel.FUNCTIONAL, PrivacyLevel.ANALYTICS, PrivacyLevel.MARKETING]
      .forEach(level => {
        this._consent[level] = ConsentStatus.GRANTED;
      });
    this._persistConsent();
    this._notifyListeners('all', ConsentStatus.GRANTED);

    // 执行所有被阻塞的回调
    for (const level of Object.keys(this._consent)) {
      this._executeBlockedCallbacks(level);
    }

    logger.info('用户已接受所有隐私同意');
  }

  /**
   * 仅接受必需项
   */
  acceptEssentialOnly() {
    [PrivacyLevel.FUNCTIONAL, PrivacyLevel.ANALYTICS, PrivacyLevel.MARKETING]
      .forEach(level => {
        this._consent[level] = ConsentStatus.DENIED;
      });
    this._persistConsent();
    this._notifyListeners('all', ConsentStatus.DENIED);

    logger.info('用户仅接受必要隐私同意');
  }

  /**
   * 撤回所有同意
   */
  withdrawAll() {
    [PrivacyLevel.FUNCTIONAL, PrivacyLevel.ANALYTICS, PrivacyLevel.MARKETING]
      .forEach(level => {
        this._consent[level] = ConsentStatus.WITHDRAWN;
      });
    this._persistConsent();
    this._notifyListeners('all', ConsentStatus.WITHDRAWN);

    logger.info('用户已撤回所有隐私同意');
  }

  /**
   * 在获得同意后执行回调
   * 如果已同意则立即执行，否则挂起等待同意
   * 
   * @param {string} level - 所需的隐私等级
   * @param {function} callback - 回调函数
   * @param {string} sdkName - SDK 标识 (用于调试)
   */
  withConsent(level, callback, sdkName = 'unknown') {
    if (this.hasConsent(level)) {
      callback();
    } else {
      // 挂起回调，等待同意
      if (!this._blockedCallbacks.has(level)) {
        this._blockedCallbacks.set(level, []);
      }
      this._blockedCallbacks.get(level).push({ callback, sdkName });
      logger.debug(`[${sdkName}] 功能已挂起，等待 ${level} 隐私同意`);
    }
  }

  /**
   * 订阅同意变更
   * @param {function} listener - (level, newStatus, oldStatus) => void
   * @returns {function} 取消订阅函数
   */
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /**
   * 按隐私等级订阅同意变更
   * 比 subscribe 更精准 — 只在指定等级变更时触发
   * 
   * @param {string} level - 隐私等级 (analytics/functional/marketing)
   * @param {function} callback - (granted: boolean) => void
   * @returns {function} 取消订阅函数
   * 
   * @example
   * consentManager.onConsentChange('analytics', (granted) => {
   *   if (granted) startTracking();
   *   else stopTracking();
   * });
   */
  onConsentChange(level, callback) {
    const wrapper = (changedLevel, newStatus) => {
      // 精确匹配 或 全局变更(acceptAll/withdrawAll)
      if (changedLevel === level || changedLevel === 'all') {
        const granted = changedLevel === 'all' 
          ? this.hasConsent(level) 
          : newStatus === ConsentStatus.GRANTED;
        try {
          callback(granted);
        } catch (e) {
          logger.error(`[ConsentManager] onConsentChange 回调错误 (${level}):`, e);
        }
      }
    };
    this._listeners.add(wrapper);
    return () => this._listeners.delete(wrapper);
  }

  /**
   * 获取隐私摘要 — 用于展示给用户
   * @returns {object} 
   */
  getPrivacySummary() {
    return {
      version: CURRENT_PRIVACY_VERSION,
      consent: this.getAllConsent(),
      decided: this.hasUserDecided(),
      timestamp: typeof window !== 'undefined' 
        ? localStorage.getItem(CONSENT_TIMESTAMP_KEY) 
        : null,
      categories: {
        [PrivacyLevel.ESSENTIAL]: {
          label: 'Essential',
          labelZh: '核心必需',
          description: 'Required for basic functionality like security and authentication.',
          descriptionZh: '安全认证等基础功能所必需，不可关闭。',
          required: true,
        },
        [PrivacyLevel.FUNCTIONAL]: {
          label: 'Functional',
          labelZh: '功能增强',
          description: 'Enables enhanced features like language preferences and theme settings.',
          descriptionZh: '语言偏好、主题设置等功能增强。',
          required: false,
        },
        [PrivacyLevel.ANALYTICS]: {
          label: 'Analytics',
          labelZh: '数据分析',
          description: 'Helps us understand usage patterns to improve the product.',
          descriptionZh: '帮助我们了解使用模式以改进产品。',
          required: false,
        },
        [PrivacyLevel.MARKETING]: {
          label: 'Marketing',
          labelZh: '营销推广',
          description: 'Used for personalized content and advertising.',
          descriptionZh: '用于个性化内容和广告推送。',
          required: false,
        },
      },
    };
  }

  // ============ 内部方法 ============

  _persistConsent() {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(this._consent));
      localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
      localStorage.setItem(CONSENT_VERSION_KEY, CURRENT_PRIVACY_VERSION);
    } catch (e) {
      logger.warn('无法持久化隐私同意:', e);
    }
  }

  _clearStoredConsent() {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
      localStorage.removeItem(CONSENT_VERSION_KEY);
    } catch (e) { /* ignore */ }
  }

  _notifyListeners(level, newStatus, oldStatus) {
    this._listeners.forEach(listener => {
      try {
        listener(level, newStatus, oldStatus);
      } catch (e) {
        logger.error('隐私同意监听器执行错误:', e);
      }
    });

    // 触发全局事件
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('qbit:consent-change', {
          detail: { level, status: newStatus, previousStatus: oldStatus }
        }));
      } catch (e) { /* ignore */ }
    }
  }

  _executeBlockedCallbacks(level) {
    const callbacks = this._blockedCallbacks.get(level) || [];
    callbacks.forEach(({ callback, sdkName }) => {
      try {
        logger.debug(`[${sdkName}] 同意已获得，执行挂起的功能`);
        callback();
      } catch (e) {
        logger.error(`[${sdkName}] 挂起回调执行错误:`, e);
      }
    });
    this._blockedCallbacks.delete(level);
  }

  // ============ 审计日志 ============

  /**
   * 添加审计条目
   * @param {string} action - 操作类型
   * @param {string} detail - 详细信息
   * @private
   */
  _addAuditEntry(action, detail) {
    if (!this._auditLog) this._auditLog = [];
    this._auditLog.push({
      action,
      detail,
      timestamp: new Date().toISOString(),
      consent: { ...this._consent },
    });
    // 保留最近 50 条
    if (this._auditLog.length > 50) {
      this._auditLog = this._auditLog.slice(-50);
    }
  }

  /**
   * 获取同意审计日志 — 用于 GDPR 数据导出
   * @returns {Array} 审计条目
   */
  getConsentLog() {
    return this._auditLog || [];
  }

  /**
   * 是否检测到 GPC 信号
   * @returns {boolean}
   */
  get isGPCDetected() {
    return this._gpcDetected === true;
  }

  /**
   * 是否检测到 DNT 信号
   * @returns {boolean}
   */
  get isDNTDetected() {
    if (typeof navigator === 'undefined') return false;
    return navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes';
  }
}

// ============ 单例导出 ============

export const consentManager = new ConsentManagerClass();

// ============ React Hook ============

import { useState, useEffect, useCallback } from 'react';

/**
 * React Hook: 隐私同意管理
 * 
 * @example
 * const { consent, hasConsent, setConsent, acceptAll } = useConsent();
 * 
 * if (hasConsent('analytics')) {
 *   // 可以进行数据追踪
 * }
 */
export function useConsent() {
  const [consent, setConsentState] = useState(() => consentManager.getAllConsent());

  useEffect(() => {
    consentManager.init();
    setConsentState(consentManager.getAllConsent());

    const unsubscribe = consentManager.subscribe(() => {
      setConsentState(consentManager.getAllConsent());
    });
    return unsubscribe;
  }, []);

  const setConsentLevel = useCallback((level, granted) => {
    consentManager.setConsent(level, granted);
  }, []);

  const acceptAllFn = useCallback(() => {
    consentManager.acceptAll();
  }, []);

  const acceptEssentialFn = useCallback(() => {
    consentManager.acceptEssentialOnly();
  }, []);

  const withdrawFn = useCallback(() => {
    consentManager.withdrawAll();
  }, []);

  return {
    consent,
    hasConsent: (level) => consentManager.hasConsent(level),
    hasUserDecided: consentManager.hasUserDecided(),
    setConsent: setConsentLevel,
    acceptAll: acceptAllFn,
    acceptEssentialOnly: acceptEssentialFn,
    withdrawAll: withdrawFn,
    summary: consentManager.getPrivacySummary(),
  };
}
